import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControlOptions,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { ApiResponse } from '@core/models/api-response.model';
import { Address } from '@src/app/core/models/address.model';
import {
  DropdownItem,
  DropDownList,
} from '@src/app/core/models/drop-down-list.model';
import { ApiCommonService } from '@src/app/core/services/api-common.service';
import { BaseValidate } from '@src/app/features/web-submission/models/base-validate.model';
import { SystemParameter } from '@src/app/features/web-submission/models/system-parameter.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AddressService } from 'sigv-library';
import { ErrorDialogMessage } from 'sigv-library/model';

@Component({
  selector: 'app-address-input',
  templateUrl: './address-input.component.html',
  styleUrls: ['./address-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: AddressInputComponent,
    },
  ],
})
export class AddressInputComponent
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor, BaseValidate
{
  @Input() isRequired = false;
  @Input() isSubmited?: boolean;
  @Input() model?: Address;
  @Output() modelChange = new EventEmitter<Address>();
  @Output() addressValueChange = new EventEmitter<Address>();

  displayLevel = 0;

  @Input() level1Options: SystemParameter[] = [];
  level2Options: SystemParameter[] = [];
  level3Options: SystemParameter[] = [];
  level4Options: SystemParameter[] = [];
  postCodeOptions: SystemParameter[] = [];

  addressForm: FormGroup;

  private readonly destroy$ = new Subject();

  disabled = false;
  touched = false;
  onChange = (_: Address) => {};
  onTouched = () => {};

  constructor(
    protected addressService: AddressService,
    protected formBuilder: FormBuilder,
    private apiCommonService: ApiCommonService
  ) {
    this.displayLevel = addressService.getSelectLevelCountByCountyId();
    this.addressForm = formBuilder.group(
      {
        level1: [''],
        level2: [''],
        level3: [''],
        level4: [''],
        postCode: [''],
        content: [''],
      },
      {
        updateOn: 'submit',
      } as AbstractControlOptions
    );
  }

  validate(): {
    validate: boolean;
    errorMsg?: ErrorDialogMessage[] | undefined;
  } {
    const errorMsg: ErrorDialogMessage[] = [];
    Object.keys(this.addressForm.controls).forEach((key) => {
      this.addressForm.get(key)?.markAsTouched();
      this.addressForm.get(key)?.markAsDirty();
      this.addressForm.get(key)?.updateValueAndValidity();
      if (this.addressForm.get(key)?.invalid) {
        errorMsg.push({ description: 'inValid' });
      }
    });

    if (errorMsg.length > 0) {
      return { validate: false, errorMsg };
    }
    return { validate: true };
  }

  writeValue(obj: Address): void {
    if (obj) {
      this.addressForm.setValue(obj, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.addressForm.disable();
    } else {
      this.addressForm.enable();
    }
  }

  ngOnInit(): void {
    this.updateFormGroupValue();
    this.initSelectOptions();
    this.addressForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.addressValueChange.emit(response);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.model) {
      // detect model change, init form group and dropdown options
      const originData: Address = changes.model.previousValue;
      const newData: Address = changes.model.currentValue;
      if (originData && newData) {
        if (
          originData.level1 === newData.level1 &&
          originData.level2 === newData.level2 &&
          originData.level3 === newData.level3 &&
          originData.level4 === newData.level4 &&
          originData.postcode === newData.postcode
        ) {
          if (originData.content !== newData.content) {
            this.updateFormGroupValue();
          }
        } else {
          this.updateFormGroupValue();
          if (originData.level1 !== newData.level1) {
            this.initLevel2Options();
          }

          if (originData.level2 !== newData.level2) {
            this.initLevel3Options();
            this.initPostCodeOptions();
          }

          if (originData.level3 !== newData.level3) {
            this.initLevel4Options();
          }
        }
      }
    }

    if (changes.isRequired) {
      Object.keys(this.addressForm.controls).forEach((key) =>
        this.addressForm.get(key)?.updateValueAndValidity()
      );
    }
  }

  level1ChangeHandler(args: { value: string }): void {
    const { value } = args;
    if (value) {
      this.apiCommonService
        .getDropdownList(value, 'AddressLevel2')
        .pipe(takeUntil(this.destroy$))
        .subscribe((response) => {
          this.level2Options = this.parseSystemParameter(response);
        });
    } else {
      this.level2Options = [];
    }

    this.addressForm.patchValue({
      level1: value ?? '',
      level2: '',
      level3: '',
      level4: '',
      postCode: '',
    });
    this.modelChange.emit({ ...this.addressForm.getRawValue() });
    this.onChange({ ...this.addressForm.getRawValue() });
  }

  level2ChangeHandler(args: { value: string }): void {
    const { value } = args;
    if (this.displayLevel > 2 && value) {
      this.apiCommonService
        .getDropdownList(value, 'AddressLevel3')
        .pipe(takeUntil(this.destroy$))
        .subscribe((response) => {
          this.level3Options = this.parseSystemParameter(response);
        });

      this.initPostCodeOptions(value);
    } else {
      this.level3Options = [];
      this.postCodeOptions = [];
    }
    this.addressForm.patchValue({
      level2: value ?? '',
      postCode: '',
      level3: '',
      level4: '',
    });
    this.modelChange.emit({ ...this.addressForm.getRawValue() });
    this.onChange({ ...this.addressForm.getRawValue() });
  }

  level3ChangeHandler(args: { value: string }): void {
    const { value } = args;
    if (this.displayLevel > 3 && value) {
      this.apiCommonService
        .getDropdownList(value, 'AddressLevel4')
        .pipe(takeUntil(this.destroy$))
        .subscribe((response) => {
          this.level4Options = this.parseSystemParameter(response);
        });
    } else {
      this.level4Options = [];
    }
    this.addressForm.patchValue({
      level3: value ?? '',
      level4: '',
    });
    this.modelChange.emit({ ...this.addressForm.getRawValue() });
    this.onChange({ ...this.addressForm.getRawValue() });
  }

  level4ChangeHandler(args: { value: string }): void {
    const { value } = args;
    this.addressForm.patchValue({
      level4: value ?? '',
    });
    this.modelChange.emit({ ...this.addressForm.getRawValue() });
    this.onChange({ ...this.addressForm.getRawValue() });
  }

  postCodeChangeHandler(args: { value: string }): void {
    const { value } = args;
    this.addressForm.patchValue({
      postCode: value ?? '',
    });
    this.modelChange.emit({ ...this.addressForm.getRawValue() });
    this.onChange({ ...this.addressForm.getRawValue() });
  }

  contentChangeHandler($event: any): void {
    const value = $event.target.value;
    this.addressForm.patchValue({
      content: value ?? '',
    });
    this.modelChange.emit({ ...this.addressForm.getRawValue() });
    this.onChange({ ...this.addressForm.getRawValue() });
  }

  private initSelectOptions(): void {
    this.initLevel2Options();
    this.initLevel3Options();
    this.initLevel4Options();
    this.initPostCodeOptions();
  }

  private initLevel4Options(): void {
    if (
      this.model?.level1 &&
      this.model?.level2 &&
      this.model?.level3 &&
      this.displayLevel > 3
    ) {
      this.apiCommonService
        .getDropdownList(this.model.level3, 'AddressLevel4')
        .pipe(takeUntil(this.destroy$))
        .subscribe((response) => {
          this.level4Options = this.parseSystemParameter(response);
        });
    }
  }

  private initLevel3Options(): void {
    if (this.model?.level1 && this.model?.level2 && this.displayLevel > 2) {
      this.apiCommonService
        .getDropdownList(this.model.level2, 'AddressLevel3')
        .pipe(takeUntil(this.destroy$))
        .subscribe((response) => {
          this.level3Options = this.parseSystemParameter(response);
        });
    }
  }

  private initLevel2Options(): void {
    if (this.model?.level1 && this.displayLevel > 1) {
      this.apiCommonService
        .getDropdownList(this.model.level1, 'AddressLevel2')
        .pipe(takeUntil(this.destroy$))
        .subscribe((response) => {
          this.level2Options = this.parseSystemParameter(response);
        });
    }
  }

  private initPostCodeOptions(addressCode = this.model?.level2): void {
    if (addressCode) {
      this.apiCommonService
        .getPostcodesByAddressCode(addressCode)
        .pipe(takeUntil(this.destroy$))
        .subscribe(({ data, rtnCode }) => {
          if (data && rtnCode === 0) {
            this.postCodeOptions = data.map((value) => ({
              name: `00000${value}`.slice(-5),
              value,
            })) as SystemParameter[];
          }
        });
    }
  }

  private updateFormGroupValue(): void {
    this.addressForm.patchValue({
      level1: this.model?.level1 ?? '',
      level2: this.model?.level2 ?? '',
      level3: this.model?.level3 ?? '',
      level4: this.model?.level4 ?? '',
      postCode: this.model?.postcode ?? '',
      content: this.model?.content ?? '',
    });
  }

  private parseSystemParameter(
    response: ApiResponse<DropDownList[]>
  ): DropdownItem[] {
    if (response && response.rtnCode === 0 && response.data) {
      return response.data
        .reduce((pre, curr) => {
          const list = curr.sysParams.filter((item) => !!item.isEnable);
          return [...pre, ...list];
        }, [] as DropdownItem[])
        .sort((a, b) => a.order - b.order);
    } else {
      return [];
    }
  }
}
